'use client';

import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Coins, Plus, Minus, Loader2 } from 'lucide-react';
import { useCustomerCredits, useAdjustCustomerCredits, type CustomerDetail } from '@/lib/api/customers';
import { useTranslation } from 'react-i18next';
import { DateDisplay } from '@/components/date-display';

interface CustomerCreditsProps {
    customer: CustomerDetail;
}

export function CustomerCredits({ customer }: CustomerCreditsProps) {
    const { t } = useTranslation('customers');
    const { data: credits, isLoading } = useCustomerCredits(customer.id);
    const adjustMutation = useAdjustCustomerCredits();

    const [amount, setAmount] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [mode, setMode] = React.useState<'add' | 'remove'>('add');

    const handleSubmit = async () => {
        const numAmount = parseInt(amount, 10);
        if (!numAmount || !description.trim()) return;

        try {
            await adjustMutation.mutateAsync({
                customerId: customer.id,
                amount: mode === 'remove' ? -numAmount : numAmount,
                description: description.trim(),
            });
            setAmount('');
            setDescription('');
        } catch {
            // Error handled by hook toast
        }
    };

    if (isLoading) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                        <Coins className="h-4 w-4" />
                        {t('credits.title')}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                    <Coins className="h-4 w-4" />
                    {t('credits.title')}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Balance */}
                <div className="text-center">
                    <p className="text-3xl font-bold">{credits?.balance ?? 0}</p>
                    <p className="text-muted-foreground text-sm">{t('credits.currentBalance')}</p>
                </div>

                <div className="flex justify-between text-sm text-muted-foreground">
                    <span>{t('credits.earned', { amount: credits?.lifetimeEarned ?? 0 })}</span>
                    <span>{t('credits.spent', { amount: credits?.lifetimeSpent ?? 0 })}</span>
                </div>

                <Separator />

                {/* Adjustment form */}
                <div className="space-y-3">
                    <p className="text-sm font-medium">{t('credits.manualAdjustment')}</p>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant={mode === 'add' ? 'default' : 'outline'}
                            onClick={() => setMode('add')}
                        >
                            <Plus className="mr-1 h-3 w-3" /> {t('credits.add')}
                        </Button>
                        <Button
                            size="sm"
                            variant={mode === 'remove' ? 'default' : 'outline'}
                            onClick={() => setMode('remove')}
                        >
                            <Minus className="mr-1 h-3 w-3" /> {t('credits.remove')}
                        </Button>
                    </div>
                    <Input
                        type="number"
                        min="1"
                        placeholder={t('credits.amountPlaceholder')}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                    />
                    <Input
                        placeholder={t('credits.reasonPlaceholder')}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                    <Button
                        className="w-full"
                        size="sm"
                        disabled={!amount || !description.trim() || adjustMutation.isPending}
                        onClick={handleSubmit}
                    >
                        {adjustMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {mode === 'add' ? t('credits.addCredits') : t('credits.removeCredits')}
                    </Button>
                </div>

                {/* Recent transactions */}
                {credits?.transactions && credits.transactions.length > 0 && (
                    <>
                        <Separator />
                        <div className="space-y-2">
                            <p className="text-sm font-medium">{t('credits.recentHistory')}</p>
                            {credits.transactions.slice(0, 10).map((tx, i) => (
                                <div key={tx.id}>
                                    {i > 0 && <Separator className="my-2" />}
                                    <div className="flex items-center justify-between text-sm">
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate">{tx.description}</p>
                                            <p className="text-muted-foreground text-xs">
                                                <DateDisplay value={tx.createdAt} mode="date" fallback="-" />
                                            </p>
                                        </div>
                                        <Badge variant={tx.amount > 0 ? 'green' : 'red'} className="ml-2">
                                            {tx.amount > 0 ? '+' : ''}
                                            {tx.amount}
                                        </Badge>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
